import * as d3 from "d3"

export default class D3HorizontalMultiBarChart {

  constructor(containerSelector, data, opts={}) {
    this.container = d3.select(containerSelector)
    this.data = data
    this.opts = opts
    this.colors = this.opts.colors || ['#3288BD']
    this._loadMargins()
    this._loadCharts()
    this._loadAxis()
    this._loadFooter()
  }

  _loadBox(svg) {
    let box = {}
    if(window.forPrint) {
      let boxHeight = $(svg.node()).data('box-height')
      let boxWidth = $(svg.node()).data('box-width')
      box = {width: boxWidth, height: boxHeight}
    } else {
      box = svg.node().getBoundingClientRect()
    }
    return box
  }

  _loadMargins() {
    this.margins = {}
    this.margins.axis = {left: 0, top: 26, right: 15, bottom: 0}
    this.margins.chart = {left: 0, top: 26, right: 40, bottom: 68}
    this.margins.footer = {left: 0, top: 0, right: 40, bottom: 0}
  }

  _loadDimensions(box, margins) {
    return {
      chart: {
        width: box.width - margins.left - margins.right,
        height: box.height - margins.top - margins.bottom,
        transform: "translate("+margins.left +", "+margins.top+")"
      },
      svg: {
        width: box.width,
        height: box.height
      }
    }
  }

  _loadRange(dimensions) {
    return {
      x: [0, dimensions.chart.width],
      y: [0, dimensions.chart.height],
      z: this.colors
    }
  }

  _loadDomain(bars) {
    return {
      x: [0, 100],
      y: this.data.terms,
      z: [1]
    }
  }

  _loadScale(domain, range) {
    return {
      x: d3.scaleLinear().domain(domain.x).range(range.x),
      y: d3.scaleBand().domain(domain.y).range(range.y).paddingInner(0.25).paddingOuter(0.25),
      z: d3.scaleOrdinal().domain(domain.z).range(range.z)
    }
  }

  _loadAxis() {
    this.axis = this.data.rows.map((d) => {
      const svg = this.container.select('[data-row="'+d.label_key+'"] svg.axis')
      const box = this._loadBox(svg, d)
      const dimensions = this._loadDimensions(box, this.margins.axis)
      const range = this._loadRange(dimensions)
      const domain = this._loadDomain(d.bars)
      const scale = this._loadScale(domain, range)
      const data = this.data.terms.map((term) => {
        let datum = {
          term: term,
          values: [{value: term, class: 'label-sm'}, {value: '0% (0)', class: 'value'}]
        }
        const bar = d.bars.filter((bar) => bar.year == term)[0]
        if(bar){
          datum.values = [{value: term, class: 'label-sm'}, {value: `${bar.percentage}% (${bar.count})`, class: 'value'}]
        }
        return datum
      })
      return {
        svg: svg,
        data: data,
        dimensions: dimensions,
        range: range,
        domain: domain,
        scale: scale,
        label: d.label,
        label_key: d.label_key,
        change: d.change
      }
    })
  }

  _loadCharts() {
    this.charts = this.data.rows.map((d) => {
      const svg = this.container.select('[data-row="'+d.label_key+'"] svg.chart')
      const box = this._loadBox(svg, d)
      const dimensions = this._loadDimensions(box, this.margins.chart)
      const range = this._loadRange(dimensions)
      const domain = this._loadDomain(d.bars)
      const scale = this._loadScale(domain, range)
      return {
        svg: svg,
        data: d.bars,
        dimensions: dimensions,
        range: range,
        domain: domain,
        scale: scale
      }
    })
  }

  _drawAxis() {
    this.axis.forEach((axis) => {
      axis.svg.append('g')
        .attr('class', 'y-axis-labels')
        .append('g')
          .attr('class', 'y-axis-label')
          .append('text')
            .attr('class', 'label')
            .attr('x', axis.dimensions.chart.width)
            .attr('y', this.margins.axis.top - 5)
            .attr('text-anchor', 'end')
            .text(axis.label)
      axis.container = axis.svg.append('g')
        .attr('class', 'd3-chart')
        .attr('transform', axis.dimensions.chart.transform)
      axis.container.append('g')
        .attr('class', 'y-axis-labels')
        .append('g')
          .attr('class', 'y-axis-label')
          .selectAll('text')
            .data(axis.data)
            .enter()
            .append('text')
              .attr('class', 'value')
              .attr('x', (d) => axis.dimensions.chart.width-12)
              .attr('y', (d) => axis.scale.y(d.term)+(axis.scale.y.bandwidth()/2))
              .attr('text-anchor', 'end')
              .selectAll('tspan')
                .data((d) => d.values)
                .enter()
                .append('tspan')
                  .attr('class', (d) => d.class)
                  .attr('alignment-baseline', 'middle')
                  .attr('dx', 12)
                  .text((d) => d.value)
    })
  }

  _drawBars(chart) {
    chart.container.selectAll('rect')
      .data(chart.data)
      .enter()
      .append('rect')
        .attr('x', (d) => chart.scale.x(0))
        .attr('y', (d) => chart.scale.y(d.year))
        .attr('width', (d) => chart.scale.x(d.percentage))
        .attr('height', (d) => chart.scale.y.bandwidth())
        .attr('fill', (d) => chart.scale.z(1))
    chart.container.append('g')
      .attr('class', 'bar-labels')
      .selectAll('text')
        .data(chart.data)
        .enter()
        .append('text')
          .attr('x', (d) => chart.scale.x(d.percentage)+5)
          .attr('y', (d) => chart.scale.y(d.year)+(chart.scale.y.bandwidth()/2))
          .attr('alignment-baseline', 'middle')
          .text((d) => `${d.percentage}% (${d.count})`)

  }

  _drawGraph(chart) {
    const data = d3.range(0, 110, 10)
    chart.svg.append('g')
      .attr('class', 'graph-lines vertical')
      .selectAll('line')
        .data(data)
        .enter()
        .append('line')
          .attr('x1', (d) => chart.scale.x(d))
          .attr('y1', 0)
          .attr('x2', (d) => chart.scale.x(d))
          .attr('y2', chart.dimensions.svg.height)
  }

  _drawCharts() {
    this.charts.forEach((chart) => {
      this._drawGraph(chart)
      chart.container = chart.svg.append('g')
        .attr('class', 'd3-chart')
        .attr('transform', chart.dimensions.chart.transform)
      this._drawBars(chart)
    })
  }

  _loadFooter() {
    const svg = this.container.select('tfoot svg')
    const box = this._loadBox(svg)
    if(window.forPrint) {
      box.height = 26
    }
    const dimensions = this._loadDimensions(box, this.margins.footer)
    const range = this._loadRange(dimensions)
    const domain = this._loadDomain([])
    const scale = this._loadScale(domain, range)
    this.footer = {
      svg: svg,
      data: this.data.scores,
      dimensions: dimensions,
      range: range,
      domain: domain,
      scale: scale
    }
  }

  _drawFooter() {
    const data = d3.range(0, 110, 10)
    this.footer.svg.selectAll('text')
      .data(data)
      .enter()
      .append('text')
        .attr('x', (d) => this.footer.scale.x(d))
        .attr('y', this.footer.dimensions.chart.height)
        .attr('text-anchor', (d, i) => i == 0 ? 'start' : 'middle')
        .text((d, i) =>  i == 0 ? d : d+"%")
  }

  draw() {
    this._drawAxis()
    this._drawCharts()
    this._drawFooter()
  }

}
